/**
 * Draft mutations for path lander / offer lists (Standard tab).
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  VOLUUM_PATH_ITEM_WEIGHT_MAX,
  type VoluumPathLander,
  type VoluumPathOffer,
  type VoluumPathUpdate,
} from '@trafficpulse/shared';

import type { PathItemKind } from '../../utils/path-item-display';

export function pathItemsSetWeight(
  onPatch: (mutator: (p: VoluumPathUpdate) => void) => void,
  kind: PathItemKind,
  idx: number,
  weight: number,
): void {
  onPatch((p) => {
    const list = p[kind] as Array<{ weight: number }> | undefined;
    const target = list?.[idx];
    if (target) {
      target.weight = Math.max(
        0,
        Math.min(VOLUUM_PATH_ITEM_WEIGHT_MAX, Math.round(weight)),
      );
    }
  });
}

export function pathItemsRemoveAt(
  onPatch: (mutator: (p: VoluumPathUpdate) => void) => void,
  kind: PathItemKind,
  idx: number,
): void {
  onPatch((p) => {
    const list = [...(p[kind] as unknown[])];
    list.splice(idx, 1);
    (p as VoluumPathUpdate)[kind] = list as never;
  });
}

export function pathItemsDuplicateAt(
  onPatch: (mutator: (p: VoluumPathUpdate) => void) => void,
  kind: PathItemKind,
  idx: number,
): void {
  onPatch((p) => {
    const list = [...(p[kind] as unknown[])];
    const row = list[idx];
    if (row === undefined) return;
    list.splice(idx + 1, 0, structuredClone(row) as object);
    (p as VoluumPathUpdate)[kind] = list as never;
  });
}

export function pathItemsCommitAdd(
  onPatch: (mutator: (p: VoluumPathUpdate) => void) => void,
  kind: PathItemKind,
  payload: { entityId: string; displayName: string },
): void {
  onPatch((p) => {
    const list = [...((p[kind] as unknown[]) ?? [])];
    if (kind === 'landers') {
      const row: VoluumPathLander = {
        lander: { id: payload.entityId },
        weight: 100,
        sublanders: [],
        ...(payload.displayName ? { landerName: payload.displayName } : {}),
      };
      list.push(row);
    } else {
      const row: VoluumPathOffer = {
        offer: { id: payload.entityId },
        weight: 100,
        ...(payload.displayName ? { offerName: payload.displayName } : {}),
      };
      list.push(row);
    }
    p[kind] = list as never;
  });
}

export function pathItemsCommitEdit(
  onPatch: (mutator: (p: VoluumPathUpdate) => void) => void,
  kind: PathItemKind,
  index: number,
  payload: { entityId: string; displayName: string },
): void {
  onPatch((p) => {
    const list = p[kind] as VoluumPathLander[] | VoluumPathOffer[] | undefined;
    const row = list?.[index];
    if (!row) return;
    if (kind === 'landers') {
      const lr = row as VoluumPathLander;
      lr.lander = { ...lr.lander, id: payload.entityId };
      if (payload.displayName) lr.landerName = payload.displayName;
      else delete lr.landerName;
      return;
    }
    const offerRow = row as VoluumPathOffer;
    offerRow.offer = { ...offerRow.offer, id: payload.entityId };
    if (payload.displayName) offerRow.offerName = payload.displayName;
    else delete offerRow.offerName;
  });
}

export function pathItemsSetLanderDisplayName(
  onPatch: (mutator: (p: VoluumPathUpdate) => void) => void,
  index: number,
  landerName: string,
): void {
  onPatch((p) => {
    const row = p.landers?.[index] as VoluumPathLander | undefined;
    if (!row) return;
    const name = landerName.trim();
    if (name) row.landerName = name;
    else delete row.landerName;
  });
}
